import React, { useMemo, useState } from 'react';
import {
  answerClearstepQuestion,
  findComplaints,
  startClearstepConversation,
} from './clearstepApi';
import './ClearstepSymptomChecker.css';

function calculateClearstepAge(dateOfBirth) {
  if (!dateOfBirth) return undefined;

  const birthDate = new Date(dateOfBirth);
  if (Number.isNaN(birthDate.getTime())) return undefined;

  const today = new Date();
  let years = today.getFullYear() - birthDate.getFullYear();
  const monthDelta = today.getMonth() - birthDate.getMonth();
  const dayDelta = today.getDate() - birthDate.getDate();

  if (monthDelta < 0 || (monthDelta === 0 && dayDelta < 0)) years -= 1;

  if (years > 0) return { years };

  const months = Math.max(
    1,
    (today.getFullYear() - birthDate.getFullYear()) * 12 + today.getMonth() - birthDate.getMonth()
  );
  return { months };
}

const STEPS = [
  { key: 'symptom', label: 'Symptom' },
  { key: 'match', label: 'Confirm' },
  { key: 'questions', label: 'Questions' },
  { key: 'recommendation', label: 'Next step' },
];

function normalizeClearstepSex(value) {
  if (!value) return '';
  const normalized = String(value).trim().toLowerCase();

  if (['m', 'male', 'man'].includes(normalized)) return 'male';
  if (['f', 'female', 'woman'].includes(normalized)) return 'female';

  if (normalized.startsWith('m')) return 'male';
  if (normalized.startsWith('f')) return 'female';

  return normalized;
}

export default function ClearstepSymptomChecker({
  patient,
  patientLoading = false,
  onTriageComplete,
  onCancel,
  onContinueAfterTriage,
  continueLabel = 'Continue',
  title = 'Symptom Checker',
  description = 'Answer a few structured questions so your care request can be routed to the right next step.',
}) {
  const [complaint, setComplaint] = useState('');
  const [complaints, setComplaints] = useState([]);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [notes, setNotes] = useState(null);
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [responses, setResponses] = useState([]);
  const [triage, setTriage] = useState(null);
  const [showEducation, setShowEducation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [demographicSex, setDemographicSex] = useState('');

  const clearstepAge = useMemo(
    () => calculateClearstepAge(patient?.dateOfBirth),
    [patient?.dateOfBirth]
  );

  const clearstepSex = normalizeClearstepSex(patient?.gender || demographicSex);

  const reset = () => {
    setComplaint('');
    setComplaints([]);
    setSelectedComplaint(null);
    setConversationId(null);
    setNotes(null);
    setQuestion(null);
    setAnswer(null);
    setResponses([]);
    setTriage(null);
    setShowEducation(false);
    setError('');
  };

  const checkComplaint = async () => {
    if (!complaint.trim()) {
      setError('Please enter your current health concern.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const matches = await findComplaints(complaint.trim());
      if (!Array.isArray(matches) || matches.length === 0) {
        setError(`No matching symptoms were found for "${complaint}". Please try again.`);
        setComplaints([]);
        return;
      }
      setComplaints(matches);
    } catch (err) {
      setError(err.message || 'Unable to search symptoms right now.');
    } finally {
      setLoading(false);
    }
  };

  const startConversation = async (match) => {
    setLoading(true);
    setError('');
    setSelectedComplaint(match);
    setComplaints([match]);

    try {
      if (patientLoading) {
        setError('Please wait while we load the patient profile.');
        return;
      }

      if (!clearstepSex) {
        setError('The patient profile does not include sex/gender. Please select it below to continue this demo.');
        return;
      }

      const result = await startClearstepConversation({
        complaintId: match.id,
        sex: clearstepSex,
        age: clearstepAge,
      });

      setConversationId(result.conversation_id);
      setNotes(result.notes || null);
      setQuestion(result.next_step?.question || null);
      setAnswer(null);
    } catch (err) {
      setError(err.message || 'Unable to start the symptom assessment right now.');
    } finally {
      setLoading(false);
    }
  };

  const toggleAnswer = (option) => {
    if (!question) return;

    if (!question.is_multi_select) {
      submitAnswer([option.option_id]);
      return;
    }

    setAnswer((current) => {
      const existing = current?.affirmed_option_ids || [];
      const optionId = option.option_id;

      if (optionId === 0) {
        return { question_id: question.question_id, affirmed_option_ids: [0], conversation_id: conversationId };
      }

      const withoutNone = existing.filter((id) => id !== 0);
      const nextIds = withoutNone.includes(optionId)
        ? withoutNone.filter((id) => id !== optionId)
        : [...withoutNone, optionId];

      return { question_id: question.question_id, affirmed_option_ids: nextIds, conversation_id: conversationId };
    });
  };

  const submitAnswer = async (optionIds) => {
    if (!question || !conversationId) return;

    const selectedIds = optionIds || answer?.affirmed_option_ids || [];
    if (selectedIds.length === 0) {
      setError('Please select at least one answer.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await answerClearstepQuestion({
        questionId: question.question_id,
        optionIds: selectedIds,
        conversationId,
      });

      setAnswer(null);
      setResponses(result.suggested_messages || []);
      setQuestion(result.question || null);
      setTriage(result.triage || null);

      if (result.triage && onTriageComplete) {
        onTriageComplete({
          triage: result.triage,
          complaint: selectedComplaint,
          conversationId,
          messages: result.suggested_messages || [],
        });
      }
    } catch (err) {
      setError(err.message || 'Unable to submit your answer right now.');
    } finally {
      setLoading(false);
    }
  };

  const selectedIds = answer?.affirmed_option_ids || [];
  const stageKey = triage
    ? 'recommendation'
    : conversationId
      ? 'questions'
      : complaints.length > 0
        ? 'match'
        : 'symptom';
  const activeStepIndex = Math.max(0, STEPS.findIndex((step) => step.key === stageKey));
  const latestSystemMessage = responses.find((message) => !message.is_from_user)?.text;

  return (
    <section className="clearstep-shell clearstep-horizontal" aria-label="Symptom checker">
      <div className="clearstep-hero">
        <div>
          <p className="clearstep-eyebrow">Guided symptom intake</p>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        <div className="clearstep-hero-actions">
          {conversationId && !triage && <button type="button" onClick={reset}>Start over</button>}
          {onCancel && <button type="button" className="clearstep-link clearstep-link-top" onClick={onCancel}>Back</button>}
        </div>
      </div>

      <div className="clearstep-flow-panel">
        <aside className="clearstep-context-panel">
          <span className="clearstep-context-kicker">Current request</span>
          <h2>{selectedComplaint?.name || complaint || 'Tell us what is happening'}</h2>
          <p>
            {triage
              ? 'Your symptom check is complete. Review the recommendation, then continue to care options.'
              : question
                ? 'Answer the current question to move the assessment forward.'
                : complaints.length > 0
                  ? 'Confirm the closest matching symptom before we ask the guided questions.'
                  : 'Start by entering the main symptom or health concern.'}
          </p>
          {patientLoading && <div className="clearstep-mini-status">Loading patient profile…</div>}
          {clearstepSex && <div className="clearstep-mini-status">Using patient profile</div>}
        </aside>

        <div className="clearstep-stage-window">
          <div key={stageKey} className="clearstep-stage-card">
            {stageKey === 'symptom' && (
              <>
                <span className="clearstep-step-label">Symptom intake</span>
                <h2>What is your current health concern?</h2>
                <p className="clearstep-muted">Enter one concern to begin. For example: sore throat, fever, ear pain, or toothache.</p>
                <div className="clearstep-search-row clearstep-search-row-modern">
                  <input
                    id="clearstep-complaint"
                    value={complaint}
                    onChange={(event) => setComplaint(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') checkComplaint();
                    }}
                    placeholder="Example: sore throat, fever, ear pain"
                    autoComplete="off"
                  />
                  <button type="button" className="clearstep-primary" onClick={checkComplaint} disabled={loading}>Check</button>
                </div>
              </>
            )}

            {stageKey === 'match' && (
              <>
                <span className="clearstep-step-label">Confirm symptom</span>
                <h2>Which symptom is the best match?</h2>
                <p className="clearstep-muted">You entered <strong>{complaint}</strong>. Choose the closest match.</p>
                <div className="clearstep-options clearstep-choice-grid">
                  {complaints.map((match) => (
                    <button key={match.id} type="button" onClick={() => startConversation(match)}>
                      {match.name}
                    </button>
                  ))}
                  <button type="button" className="clearstep-secondary" onClick={reset}>None of those fit</button>
                </div>

                {!clearstepSex && !patientLoading && (
                  <div className="clearstep-demographics">
                    <label htmlFor="clearstep-sex">Demo only: patient sex/gender</label>
                    <select
                      id="clearstep-sex"
                      value={demographicSex}
                      onChange={(event) => setDemographicSex(event.target.value)}
                    >
                      <option value="">Select…</option>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                    </select>
                    <p>This is only shown when the active patient profile does not expose this value.</p>
                  </div>
                )}
              </>
            )}

            {stageKey === 'questions' && (
              <>
                <span className="clearstep-step-label">Guided questions</span>
                {notes?.conversation_intro?.description && !responses.length && (
                  <div className="clearstep-intro-inline">{notes.conversation_intro.description}</div>
                )}
                {latestSystemMessage && <p className="clearstep-muted clearstep-latest-message">{latestSystemMessage}</p>}
                {question && !triage && (
                  <>
                    <h2>{question.text}</h2>
                    <div className="clearstep-options clearstep-choice-grid">
                      {question.options.map((option) => {
                        const selected = selectedIds.includes(option.option_id);
                        return (
                          <button
                            key={option.option_id}
                            type="button"
                            className={selected ? 'selected' : ''}
                            onClick={() => toggleAnswer(option)}
                          >
                            {question.is_multi_select && <span>{selected ? '☑' : '☐'}</span>}
                            {option.value}
                          </button>
                        );
                      })}
                    </div>
                    {question.is_multi_select && (
                      <div className="clearstep-stage-actions">
                        <button type="button" className="clearstep-primary" onClick={() => submitAnswer()} disabled={loading}>
                          Continue
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {stageKey === 'recommendation' && triage && (
              <>
                <p className="clearstep-eyebrow">Recommendation</p>
                <h2>{triage.triage_level}</h2>
                <div className="clearstep-recommendation-copy">
                  <p>{triage.primary_care_summary}</p>
                  <p>{triage.explanation_for_users}</p>
                </div>
                <div className="clearstep-stage-actions">
                  {onContinueAfterTriage && (
                    <button type="button" className="clearstep-primary clearstep-continue" onClick={onContinueAfterTriage}>
                      {continueLabel}
                    </button>
                  )}
                  {triage.educational_info && triage.educational_info.trim() !== '# Educational Information' && (
                    <button type="button" className="clearstep-secondary" onClick={() => setShowEducation((value) => !value)}>
                      {showEducation ? 'Hide details' : 'See more details'}
                    </button>
                  )}
                </div>
                {showEducation && <pre className="clearstep-education">{triage.educational_info}</pre>}
              </>
            )}
          </div>
        </div>
      </div>

      {error && <div className="clearstep-error" role="alert">{error}</div>}
      {loading && <div className="clearstep-loading">Working…</div>}
    </section>
  );
}
